import { useState, useEffect, useCallback, useRef } from 'react';
import {
    FaTimes, FaPlus, FaEdit, FaTrash, FaSave, FaToggleOn, FaToggleOff,
    FaInfoCircle, FaArrowRight, FaBolt, FaClock, FaCalendarAlt,
    FaExchangeAlt, FaHandPointer, FaUserPlus, FaComment, FaRedo,
    FaExclamationTriangle, FaGlobe, FaCopy, FaFilter, FaProjectDiagram
} from 'react-icons/fa';
import { IoSearch, IoClose } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import '@/styles/ActionItemsStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * AutomationRulesModal Component
 * Provides a user-friendly visual rule builder for board automations.
 *
 * Rules follow a "When [trigger] → Then [action]" pattern, with all configuration
 * exposed through structured form controls (dropdowns, number inputs, text fields)
 * instead of raw JSON. Designed for non-technical users.
 *
 * @param {Object}   props
 * @param {Object}   props.board          - The board object (must have boardId)
 * @param {Function} props.onClose        - Callback to close the modal
 * @param {Function} props.onRulesUpdated - Callback fired after rules are created/updated/deleted
 */
const AutomationRulesModal = ({ board, boardColumns, onClose, onRulesUpdated, inline = false }) => {
    const { apiFetch } = useAuth();
    const [rules, setRules] = useState([]);
    const [columns, setColumns] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [allDepartments, setAllDepartments] = useState([]);
    const [allRoles, setAllRoles] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showRuleForm, setShowRuleForm] = useState(false);
    const [editingRule, setEditingRule] = useState(null);

    // Searchable chip selector state
    const [userSearch, setUserSearch] = useState('');
    const [deptSearch, setDeptSearch] = useState('');
    const [roleSearch, setRoleSearch] = useState('');
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [showDeptDropdown, setShowDeptDropdown] = useState(false);
    const [showRoleDropdown, setShowRoleDropdown] = useState(false);
    const userSearchRef = useRef(null);
    const deptSearchRef = useRef(null);
    const roleSearchRef = useRef(null);

    // Structured form state — no raw JSON exposed to users
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        sourceColumnId: null,
        destinationColumnId: '',
        triggerType: 'StatusChange',
        actionType: 'MoveOnly',
        isActive: true,
        priority: 0,
        // Time-based trigger fields (user-friendly instead of JSON)
        timeValue: 7,
        timeUnit: 'days',
        // Field-change trigger fields
        fieldName: 'Priority',
        fieldValue: '',
        // Priority change trigger fields
        priorityValue: '',
        // Recurring trigger fields
        recurrenceInterval: 60,
        recurrenceUnit: 'minutes',
        // MoveAndNotify action fields
        notifyEmails: '',
        // NotifyOnly action fields
        notifyUserIds: [],
        notifyDepartmentIds: [],
        notifyRoleIds: [],
        emailTemplateKey: '',
        // MoveAndAssign action fields
        assignToUserId: '',
        // MoveAndComment / AddComment action fields
        commentText: '',
        // SetField action fields
        setFieldName: '',
        setFieldValue: '',
        // ChangePriority action fields
        newPriority: '',
        // CreateChildItem action fields
        childTitle: '',
        childAssignToDepartment: '',
        // CallWebhook action fields
        webhookUrl: '',
        webhookMethod: 'POST',
        // DuplicateItem action fields
        duplicateTitlePrefix: '[COPY] ',
        duplicateCopyComments: false,
        // Conditions (AND/OR filter groups)
        conditions: [],
        conditionLogic: 'AND',
        // Max executions per item (0 = unlimited)
        maxExecutionsPerItem: 0
    });

    // Trigger type definitions with icons and user-friendly descriptions
    const TRIGGER_TYPES = [
        {
            value: 'StatusChange',
            label: 'When an item enters a column',
            icon: <FaExchangeAlt />,
            description: 'Triggers when an action item is moved into the selected source column.',
            requiresSource: true
        },
        {
            value: 'TimeBasedAge',
            label: 'When an item has been in a column too long',
            icon: <FaClock />,
            description: 'Triggers when an action item stays in a column longer than the specified time.',
            requiresSource: true,
            requiresTime: true
        },
        {
            value: 'TimeBasedDeadline',
            label: 'When a deadline is approaching',
            icon: <FaCalendarAlt />,
            description: 'Triggers when an action item is within the specified time of its deadline.',
            requiresTime: true
        },
        {
            value: 'FieldChange',
            label: 'When a field value changes',
            icon: <FaBolt />,
            description: 'Triggers when a specific field on the action item is changed to a particular value.',
            requiresField: true
        },
        {
            value: 'OnCreate',
            label: 'When an item is created',
            icon: <FaPlus />,
            description: 'Triggers immediately when a new action item is added to this board.'
        },
        {
            value: 'OnAssignment',
            label: 'When an item is assigned',
            icon: <FaUserPlus />,
            description: 'Triggers when an action item is assigned or reassigned to a user.'
        },
        {
            value: 'OnComment',
            label: 'When a comment is added',
            icon: <FaComment />,
            description: 'Triggers when someone adds a comment to an action item on this board.'
        },
        {
            value: 'OnPriorityChange',
            label: 'When priority changes',
            icon: <FaExclamationTriangle />,
            description: 'Triggers when the priority of an action item is changed.',
            requiresPriorityValue: true
        },
        {
            value: 'Recurring',
            label: 'On a recurring schedule',
            icon: <FaRedo />,
            description: 'Runs automatically on a schedule (e.g., every hour, daily) for all items matching conditions.',
            requiresRecurrence: true
        },
        {
            value: 'Manual',
            label: 'Manually triggered by a user',
            icon: <FaHandPointer />,
            description: 'Only runs when a user explicitly clicks "Run" on this rule.'
        }
    ];

    // Action type definitions with user-friendly descriptions
    const ACTION_TYPES = [
        {
            value: 'MoveOnly',
            label: 'Move to column',
            description: 'Moves the item to the destination column.',
            category: 'Column Actions'
        },
        {
            value: 'MoveAndNotify',
            label: 'Move and send notification',
            description: 'Moves the item and sends an email notification.',
            requiresNotify: true,
            category: 'Column Actions'
        },
        {
            value: 'MoveAndAssign',
            label: 'Move and assign to user',
            description: 'Moves the item and assigns it to a specific user.',
            requiresAssign: true,
            category: 'Column Actions'
        },
        {
            value: 'MoveAndComment',
            label: 'Move and add a comment',
            description: 'Moves the item and adds an automated comment.',
            requiresComment: true,
            category: 'Column Actions'
        },
        {
            value: 'NotifyOnly',
            label: 'Send notification only',
            description: 'Sends a notification to selected users, departments, or roles without moving the item.',
            requiresRecipients: true,
            noMove: true,
            category: 'Notifications'
        },
        {
            value: 'SetField',
            label: 'Set a field value',
            description: 'Sets a field on the action item (status, priority, custom field) without moving it.',
            requiresSetField: true,
            noMove: true,
            category: 'Field Actions'
        },
        {
            value: 'AddComment',
            label: 'Add a comment only',
            description: 'Adds an automated comment to the action item without moving it.',
            requiresComment: true,
            noMove: true,
            category: 'Field Actions'
        },
        {
            value: 'ChangePriority',
            label: 'Change priority',
            description: 'Changes the priority of the action item.',
            requiresPriority: true,
            noMove: true,
            category: 'Field Actions'
        },
        {
            value: 'DuplicateItem',
            label: 'Duplicate item',
            description: 'Creates a copy of the action item in the same or a different column.',
            requiresDuplicateConfig: true,
            noMove: true,
            category: 'Item Actions'
        },
        {
            value: 'CreateChildItem',
            label: 'Create child item',
            description: 'Creates a new child action item linked to this one.',
            requiresChildConfig: true,
            noMove: true,
            category: 'Item Actions'
        },
        {
            value: 'CallWebhook',
            label: 'Call external webhook',
            description: 'Sends a POST/PUT request to an external URL with the action item data.',
            requiresWebhook: true,
            noMove: true,
            category: 'Integrations'
        }
    ];

    // Field options for FieldChange trigger and condition builder
    const FIELD_OPTIONS = [
        { value: 'Priority', label: 'Priority' },
        { value: 'Status', label: 'Status' },
        { value: 'AssignedToUserID', label: 'Assigned User' },
        { value: 'AssignedToDepartmentID', label: 'Assigned Department' },
        { value: 'ActionItemType', label: 'Action Item Type' },
        { value: 'Title', label: 'Title' },
        { value: 'Description', label: 'Description' },
        { value: 'DueDate', label: 'Due Date' },
        { value: 'StartDate', label: 'Start Date' },
        { value: 'CompletionPercentage', label: 'Completion %' },
        { value: 'CreatedByUserID', label: 'Created By' },
        { value: 'Tags', label: 'Tags' }
    ];

    // Condition operators for the condition builder
    const CONDITION_OPERATORS = [
        { value: 'equals', label: 'equals' },
        { value: 'notEquals', label: 'does not equal' },
        { value: 'contains', label: 'contains' },
        { value: 'greaterThan', label: 'is greater than' },
        { value: 'lessThan', label: 'is less than' },
        { value: 'isEmpty', label: 'is empty' },
        { value: 'isNotEmpty', label: 'is not empty' },
        { value: 'in', label: 'is one of' },
        { value: 'notIn', label: 'is not one of' }
    ];

    // Priority options for priority-related actions
    const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

    // Time unit options for time-based triggers
    const TIME_UNITS = [
        { value: 'minutes', label: 'Minutes' },
        { value: 'hours', label: 'Hours' },
        { value: 'days', label: 'Days' },
        { value: 'weeks', label: 'Weeks' }
    ];

    // Declare fetch functions with useCallback BEFORE the useEffect that calls them
    // to avoid ReferenceError from temporal dead zone
    const fetchRules = useCallback(async () => {
        try {
            const response = await apiFetch(`/api/KanbanBoards/${board.boardId}/automation-rules`);
            const result = await response.json();
            if (result.success) {
                setRules(result.data);
            } else {
                toast.error('Failed to load automation rules');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching automation rules:', error);
            toast.error('Failed to load automation rules');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, board.boardId]);

    const fetchColumns = useCallback(async () => {
        // Use columns passed from parent if available (avoids duplicate API call)
        if (boardColumns && boardColumns.length > 0) {
            setColumns(boardColumns);
            return;
        }
        try {
            const response = await apiFetch(`/api/KanbanBoards/${board.boardId}`);
            const result = await response.json();
            if (result.success) {
                setColumns(result.data.columns || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching columns:', error);
        }
    }, [apiFetch, board.boardId, boardColumns]);

    const fetchUsers = useCallback(async () => {
        try {
            const response = await apiFetch('/api/User/all');
            const result = await response.json();
            if (result.success && Array.isArray(result.data)) {
                setAllUsers(result.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching users:', error);
        }
    }, [apiFetch]);

    const fetchDepartments = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Department');
            const result = await response.json();
            if (result.success && Array.isArray(result.data)) {
                setAllDepartments(result.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching departments:', error);
        }
    }, [apiFetch]);

    const fetchRoles = useCallback(async () => {
        try {
            const response = await apiFetch('/api/User/roles');
            const result = await response.json();
            if (result.success && Array.isArray(result.data)) {
                setAllRoles(result.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching roles:', error);
        }
    }, [apiFetch]);

    const fetchEmailTemplates = useCallback(async () => {
        try {
            const response = await apiFetch('/api/EmailTemplates');
            const result = await response.json();
            if (result.success && Array.isArray(result.data)) {
                setEmailTemplates(result.data);
            }
        } catch (error) {
            // Non-critical — templates are optional, proceed without them
            console.error('Error fetching email templates:', error);
        }
    }, [apiFetch]);

    useEffect(() => {
        (async () => {
            await Promise.all([fetchRules(), fetchColumns(), fetchUsers(), fetchDepartments(), fetchRoles(), fetchEmailTemplates()]);
        })();
    }, [fetchRules, fetchColumns, fetchUsers, fetchDepartments, fetchRoles, fetchEmailTemplates]);

    /**
     * Parses a saved TriggerConfig JSON string into structured form fields.
     * Handles the mapping from stored format back to user-friendly fields.
     */
    const parseTriggerConfig = (triggerType, configJson) => {
        const result = { timeValue: 7, timeUnit: 'days', fieldName: 'Priority', fieldValue: '' };
        if (!configJson) return result;

        try {
            const config = JSON.parse(configJson);

            if (triggerType === 'TimeBasedAge' || triggerType === 'TimeBasedDeadline') {
                // Support stored formats: {ageInDays: N}, {daysBeforeDeadline: N},
                // or the structured format {timeValue: N, timeUnit: 'days'}
                if (config.timeValue && config.timeUnit) {
                    result.timeValue = config.timeValue;
                    result.timeUnit = config.timeUnit;
                } else if (config.ageInDays) {
                    result.timeValue = config.ageInDays;
                    result.timeUnit = 'days';
                } else if (config.daysBeforeDeadline) {
                    result.timeValue = config.daysBeforeDeadline;
                    result.timeUnit = 'days';
                } else if (config.ageInHours) {
                    result.timeValue = config.ageInHours;
                    result.timeUnit = 'hours';
                }
            } else if (triggerType === 'FieldChange') {
                result.fieldName = config.field || 'Priority';
                result.fieldValue = config.value || '';
            }
        } catch {
            // Invalid JSON — use defaults
        }

        return result;
    };

    /**
     * Parses a saved ActionConfig JSON string into structured form fields.
     */
    const parseActionConfig = (actionType, configJson) => {
        const result = {
            notifyEmails: '',
            assignToUserId: '',
            commentText: '',
            notifyUserIds: [],
            notifyDepartmentIds: [],
            notifyRoleIds: [],
            emailTemplateKey: '',
            duplicateTitlePrefix: '[COPY] ',
            duplicateCopyComments: false
        };
        if (!configJson) return result;

        try {
            const config = JSON.parse(configJson);

            if (actionType === 'MoveAndNotify') {
                // Support email list or user ID list
                if (config.notifyEmails) {
                    result.notifyEmails = Array.isArray(config.notifyEmails)
                        ? config.notifyEmails.join(', ')
                        : config.notifyEmails;
                } else if (config.notifyUsers && Array.isArray(config.notifyUsers)) {
                    // Legacy format: look up user emails from IDs
                    result.notifyEmails = config.notifyUsers
                        .map(userId => {
                            const user = allUsers.find(u => u.id === userId);
                            return user?.userEmail || userId;
                        })
                        .join(', ');
                }
            } else if (actionType === 'NotifyOnly') {
                result.notifyUserIds = config.notifyUserIds || [];
                result.notifyDepartmentIds = config.notifyDepartmentIds || [];
                result.notifyRoleIds = config.notifyRoleIds || [];
                result.emailTemplateKey = config.emailTemplateKey || '';
            } else if (actionType === 'MoveAndAssign') {
                result.assignToUserId = config.assignToUserId?.toString() || '';
            } else if (actionType === 'MoveAndComment') {
                result.commentText = config.comment || config.commentText || '';
            } else if (actionType === 'DuplicateItem') {
                result.duplicateTitlePrefix = config.titlePrefix || '[COPY] ';
                result.duplicateCopyComments = config.copyComments === true;
            }
        } catch {
            // Invalid JSON — use defaults
        }

        return result;
    };

    /**
     * Builds the TriggerConfig JSON string from structured form fields.
     */
    const buildTriggerConfig = () => {
        const triggerDef = TRIGGER_TYPES.find(t => t.value === formData.triggerType);

        if (triggerDef?.requiresTime) {
            return JSON.stringify({
                timeValue: parseInt(formData.timeValue) || 7,
                timeUnit: formData.timeUnit || 'days'
            });
        }

        if (triggerDef?.requiresField) {
            return JSON.stringify({
                field: formData.fieldName,
                value: formData.fieldValue
            });
        }

        if (triggerDef?.requiresPriorityValue) {
            return JSON.stringify({
                targetPriority: formData.priorityValue
            });
        }

        if (triggerDef?.requiresRecurrence) {
            return JSON.stringify({
                intervalMinutes: formData.recurrenceUnit === 'hours'
                    ? (parseInt(formData.recurrenceInterval) || 1) * 60
                    : formData.recurrenceUnit === 'days'
                        ? (parseInt(formData.recurrenceInterval) || 1) * 1440
                        : parseInt(formData.recurrenceInterval) || 60
            });
        }

        return '';
    };

    /**
     * Builds the ActionConfig JSON string from structured form fields.
     */
    const buildActionConfig = () => {
        const actionDef = ACTION_TYPES.find(a => a.value === formData.actionType);

        if (actionDef?.requiresNotify && formData.notifyEmails.trim()) {
            return JSON.stringify({
                notifyEmails: formData.notifyEmails
                    .split(',')
                    .map(e => e.trim())
                    .filter(e => e.length > 0)
            });
        }

        if (actionDef?.requiresRecipients) {
            const config = {
                notifyUserIds: formData.notifyUserIds || [],
                notifyDepartmentIds: formData.notifyDepartmentIds || [],
                notifyRoleIds: formData.notifyRoleIds || []
            };
            if (formData.emailTemplateKey) {
                config.emailTemplateKey = formData.emailTemplateKey;
            }
            return JSON.stringify(config);
        }

        if (actionDef?.requiresAssign && formData.assignToUserId) {
            return JSON.stringify({
                assignToUserId: parseInt(formData.assignToUserId)
            });
        }

        if (actionDef?.requiresComment && formData.commentText.trim()) {
            return JSON.stringify({
                comment: formData.commentText.trim()
            });
        }

        if (actionDef?.requiresSetField && formData.setFieldName) {
            return JSON.stringify({
                fieldName: formData.setFieldName,
                value: formData.setFieldValue
            });
        }

        if (actionDef?.requiresPriority && formData.newPriority) {
            return JSON.stringify({
                priority: formData.newPriority
            });
        }

        if (actionDef?.requiresDuplicateConfig) {
            return JSON.stringify({
                titlePrefix: formData.duplicateTitlePrefix || '[COPY] ',
                copyComments: formData.duplicateCopyComments === true
            });
        }

        if (actionDef?.requiresChildConfig) {
            return JSON.stringify({
                titleTemplate: formData.childTitle || 'Child of {{Title}}',
                assignToDepartment: formData.childAssignToDepartment || ''
            });
        }

        if (actionDef?.requiresWebhook && formData.webhookUrl) {
            return JSON.stringify({
                url: formData.webhookUrl,
                method: formData.webhookMethod || 'POST'
            });
        }

        return '';
    };

    /**
     * Resets the form to default state for creating a new rule.
     */
    const handleCreateRule = () => {
        setEditingRule(null);
        setFormData({
            name: '',
            description: '',
            sourceColumnId: null,
            destinationColumnId: columns.length > 0 ? columns[0].columnId : '',
            triggerType: 'StatusChange',
            actionType: 'MoveOnly',
            isActive: true,
            priority: 0,
            timeValue: 7,
            timeUnit: 'days',
            fieldName: 'Priority',
            fieldValue: '',
            priorityValue: '',
            recurrenceInterval: 60,
            recurrenceUnit: 'minutes',
            notifyEmails: '',
            notifyUserIds: [],
            notifyDepartmentIds: [],
            notifyRoleIds: [],
            emailTemplateKey: '',
            assignToUserId: '',
            commentText: '',
            setFieldName: '',
            setFieldValue: '',
            newPriority: '',
            childTitle: '',
            childAssignToDepartment: '',
            webhookUrl: '',
            webhookMethod: 'POST',
            duplicateTitlePrefix: '[COPY] ',
            duplicateCopyComments: false,
            conditions: [],
            conditionLogic: 'AND',
            maxExecutionsPerItem: 0
        });
        setShowRuleForm(true);
    };

    /**
     * Populates the form from an existing rule for editing.
     */
    const handleEditRule = (rule) => {
        setEditingRule(rule);
        const triggerFields = parseTriggerConfig(rule.triggerType, rule.triggerConfig);
        const actionFields = parseActionConfig(rule.actionType, rule.actionConfig);

        // Parse saved conditions JSON back into structured form state
        let conditions = [];
        let conditionLogic = 'AND';
        if (rule.conditions) {
            try {
                const parsed = JSON.parse(rule.conditions);
                conditions = parsed.conditions || [];
                conditionLogic = parsed.logic || 'AND';
            } catch {
                // Invalid JSON — use defaults
            }
        }

        setFormData({
            name: rule.name,
            description: rule.description || '',
            sourceColumnId: rule.sourceColumnId,
            destinationColumnId: rule.destinationColumnId,
            triggerType: rule.triggerType,
            actionType: rule.actionType,
            isActive: rule.isActive,
            priority: rule.priority,
            conditions,
            conditionLogic,
            maxExecutionsPerItem: rule.maxExecutionsPerItem ?? 0,
            ...triggerFields,
            ...actionFields
        });
        setShowRuleForm(true);
    };

    /**
     * Saves a new or updated rule to the backend.
     */
    const handleSaveRule = async () => {
        if (!formData.name.trim()) {
            toast.error('Please enter a rule name');
            return;
        }

        const currentActionDef = ACTION_TYPES.find(a => a.value === formData.actionType);

        if (!currentActionDef?.noMove && !formData.destinationColumnId) {
            toast.error('Please select a destination column');
            return;
        }

        // Validate trigger-specific required fields
        const triggerDef = TRIGGER_TYPES.find(t => t.value === formData.triggerType);
        if (triggerDef?.requiresSource && !formData.sourceColumnId) {
            toast.error('Please select a source column for this trigger type');
            return;
        }

        try {
            const url = editingRule
                ? `/api/KanbanBoards/${board.boardId}/automation-rules/${editingRule.ruleId}`
                : `/api/KanbanBoards/${board.boardId}/automation-rules`;

            const method = editingRule ? 'PUT' : 'POST';

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.name,
                    description: formData.description,
                    sourceColumnId: formData.sourceColumnId ? parseInt(formData.sourceColumnId) : null,
                    destinationColumnId: formData.destinationColumnId ? parseInt(formData.destinationColumnId) : null,
                    triggerType: formData.triggerType,
                    triggerConfig: buildTriggerConfig(),
                    actionType: formData.actionType,
                    actionConfig: buildActionConfig(),
                    isActive: formData.isActive,
                    priority: formData.priority,
                    conditions: (formData.conditions ?? []).length > 0
                        ? JSON.stringify({ logic: formData.conditionLogic, conditions: formData.conditions })
                        : null,
                    recurrenceConfig: formData.triggerType === 'Recurring' ? buildTriggerConfig() : null,
                    maxExecutionsPerItem: formData.maxExecutionsPerItem || 0
                })
            });

            const result = await response.json();

            if (result.success) {
                toast.success(result.message);
                setShowRuleForm(false);
                fetchRules();
                onRulesUpdated?.();
            } else {
                toast.error(result.message || 'Failed to save rule');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving rule:', error);
            toast.error('Failed to save rule');
        }
    };

    /**
     * Deletes a rule after user confirmation.
     */
    const handleDeleteRule = async (ruleId) => {
        if (!window.confirm('Are you sure you want to delete this automation rule?')) {
            return;
        }

        try {
            const response = await apiFetch(`/api/KanbanBoards/${board.boardId}/automation-rules/${ruleId}`, {
                method: 'DELETE'
            });

            const result = await response.json();

            if (result.success) {
                toast.success('Rule deleted successfully');
                fetchRules();
                onRulesUpdated?.();
            } else {
                toast.error(result.message || 'Failed to delete rule');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error deleting rule:', error);
            toast.error('Failed to delete rule');
        }
    };

    /**
     * Toggles a rule between active and inactive.
     */
    const handleToggleRule = async (ruleId) => {
        try {
            const response = await apiFetch(`/api/KanbanBoards/${board.boardId}/automation-rules/${ruleId}/toggle`, {
                method: 'PATCH'
            });

            const result = await response.json();

            if (result.success) {
                toast.success(result.message);
                fetchRules();
                onRulesUpdated?.();
            } else {
                toast.error(result.message || 'Failed to toggle rule');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error toggling rule:', error);
            toast.error('Failed to toggle rule');
        }
    };

    /**
     * Builds a human-readable summary of a rule for display in the rules list.
     * Example: "When an item enters 'In Progress' → Move to 'Done' and notify team"
     */
    const buildRuleSummary = (rule) => {
        const triggerDef = TRIGGER_TYPES.find(t => t.value === rule.triggerType);
        const actionDef = ACTION_TYPES.find(a => a.value === rule.actionType);

        const getTriggerText = () => {
            switch (rule.triggerType) {
                case 'StatusChange':
                    return `enters "${rule.sourceColumnName || 'any column'}"`;
                case 'TimeBasedAge': {
                    const parsed = parseTriggerConfig(rule.triggerType, rule.triggerConfig);
                    return `stays in "${rule.sourceColumnName || 'any column'}" for ${parsed.timeValue} ${parsed.timeUnit}`;
                }
                case 'TimeBasedDeadline': {
                    const parsed = parseTriggerConfig(rule.triggerType, rule.triggerConfig);
                    return `is within ${parsed.timeValue} ${parsed.timeUnit} of its deadline`;
                }
                case 'FieldChange': {
                    const parsed = parseTriggerConfig(rule.triggerType, rule.triggerConfig);
                    return `"${parsed.fieldName}" changes to "${parsed.fieldValue}"`;
                }
                case 'OnCreate':
                    return 'is created on this board';
                case 'OnAssignment':
                    return 'is assigned or reassigned';
                case 'OnComment':
                    return 'receives a new comment';
                case 'OnPriorityChange':
                    return 'priority is changed';
                case 'Recurring':
                    return 'on recurring schedule';
                case 'Manual':
                    return 'manually triggered';
                default:
                    return triggerDef?.label || rule.triggerType;
            }
        };

        const triggerText = getTriggerText();

        let actionText;
        switch (rule.actionType) {
            case 'MoveOnly':
                actionText = `move to "${rule.destinationColumnName || 'column'}"`;
                break;
            case 'MoveAndNotify':
                actionText = `move to "${rule.destinationColumnName || 'column'}" and send notification`;
                break;
            case 'MoveAndAssign':
                actionText = `move to "${rule.destinationColumnName || 'column'}" and assign to user`;
                break;
            case 'MoveAndComment':
                actionText = `move to "${rule.destinationColumnName || 'column'}" and add comment`;
                break;
            case 'NotifyOnly':
                actionText = 'send notification';
                break;
            case 'SetField':
                actionText = 'set a field value';
                break;
            case 'AddComment':
                actionText = 'add a comment';
                break;
            case 'ChangePriority':
                actionText = 'change priority';
                break;
            case 'DuplicateItem':
                actionText = 'duplicate the item';
                break;
            case 'CreateChildItem':
                actionText = 'create a child item';
                break;
            case 'CallWebhook':
                actionText = 'call external webhook';
                break;
            default:
                actionText = rule.actionType;
                break;
        }

        return { triggerText, actionText, triggerIcon: triggerDef?.icon, actionLabel: actionDef?.label };
    };

    /**
     * Updates a single field in the form state.
     */
    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    /** Toggle a chip in an array-based field (notifyUserIds, notifyDepartmentIds, notifyRoleIds) */
    const toggleChip = (field, id) => {
        const numId = parseInt(id);
        setFormData(prev => {
            const current = prev[field] || [];
            const updated = current.includes(numId)
                ? current.filter(i => i !== numId)
                : [...current, numId];
            return { ...prev, [field]: updated };
        });
    };

    /** Get display name for a user by id */
    const getUserName = (id) => {
        const u = allUsers.find(u => u.id === parseInt(id));
        return u ? (u.name || u.email || `User ${u.id}`) : `User ${id}`;
    };

    /** Get display name for a department by id */
    const getDeptName = (id) => {
        const d = allDepartments.find(d => (d.departmentID || d.id) === parseInt(id));
        return d ? (d.departmentName || d.name || `Dept ${id}`) : `Dept ${id}`;
    };

    /** Get display name for a role by id */
    const getRoleName = (id) => {
        const r = allRoles.find(r => (r.roleID || r.id) === parseInt(id));
        return r ? (r.roleName || r.name || `Role ${id}`) : `Role ${id}`;
    };

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (userSearchRef.current && !userSearchRef.current.contains(e.target)) setShowUserDropdown(false);
            if (deptSearchRef.current && !deptSearchRef.current.contains(e.target)) setShowDeptDropdown(false);
            if (roleSearchRef.current && !roleSearchRef.current.contains(e.target)) setShowRoleDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    /**
     * Gets the current trigger type definition.
     */
    const currentTrigger = TRIGGER_TYPES.find(t => t.value === formData.triggerType);

    /**
     * Gets the current action type definition.
     */
    const currentAction = ACTION_TYPES.find(a => a.value === formData.actionType);

    // ──────────────────────────────────────────────────────────────────────
    // Render: Rule Creation / Edit Form
    // ──────────────────────────────────────────────────────────────────────
    const renderRuleForm = () => (
        <div className="automation-rule-form">
            <div className="form-header">
                <h3>{editingRule ? 'Edit Automation Rule' : 'Create Automation Rule'}</h3>
                <button className="close-button-small" onClick={() => setShowRuleForm(false)}>
                    <FaTimes />
                </button>
            </div>

            <div className="form-body">
                {/* Rule Name & Description */}
                <div className="rule-builder-section rule-basics-section">
                    <div className="rule-builder-label">
                        <span className="rule-builder-step-number">1</span>
                        <span>Rule Details</span>
                    </div>
                    <div className="rule-basics-grid">
                        <div className="form-group">
                            <label htmlFor="ruleName">Rule Name *</label>
                            <input
                                id="ruleName"
                                type="text"
                                value={formData.name}
                                onChange={(e) => updateField('name', e.target.value)}
                                placeholder="e.g., Auto-close stale items"
                                maxLength={200}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="ruleDescription">Description (optional)</label>
                            <input
                                id="ruleDescription"
                                type="text"
                                value={formData.description}
                                onChange={(e) => updateField('description', e.target.value)}
                                placeholder="Briefly describe what this rule does"
                                maxLength={500}
                            />
                        </div>
                    </div>
                </div>

                {/* ── WHEN (Trigger) Section ── */}
                <div className="rule-builder-section trigger-section">
                    <div className="rule-builder-label">
                        <span className="rule-builder-step-number">2</span>
                        <span className="rule-builder-badge when-badge">WHEN</span>
                        <span>What should trigger this rule?</span>
                    </div>

                    <div className="trigger-type-grid">
                        {TRIGGER_TYPES.map(trigger => (
                            <button
                                key={trigger.value}
                                type="button"
                                className={`trigger-type-card ${formData.triggerType === trigger.value ? 'selected' : ''}`}
                                onClick={() => updateField('triggerType', trigger.value)}
                            >
                                <span className="trigger-icon">{trigger.icon}</span>
                                <span className="trigger-label">{trigger.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className="trigger-description-hint">
                        <FaInfoCircle /> {currentTrigger?.description}
                    </div>

                    {/* Source column — shown when trigger requires it */}
                    {currentTrigger?.requiresSource && (
                        <div className="form-group">
                            <label htmlFor="sourceColumn">
                                {formData.triggerType === 'TimeBasedAge'
                                    ? 'Which column should items be stuck in?'
                                    : 'Which column triggers this rule?'}
                            </label>
                            <select
                                id="sourceColumn"
                                value={formData.sourceColumnId || ''}
                                onChange={(e) => updateField('sourceColumnId', e.target.value || null)}
                            >
                                <option value="">— Select a column —</option>
                                {columns.map(col => (
                                    <option key={col.columnId} value={col.columnId}>
                                        {col.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Time value/unit — shown for time-based triggers */}
                    {currentTrigger?.requiresTime && (
                        <div className="form-group">
                            <label>
                                {formData.triggerType === 'TimeBasedDeadline'
                                    ? 'How far before the deadline?'
                                    : 'How long before triggering?'}
                            </label>
                            <div className="time-input-row">
                                <input
                                    type="number"
                                    className="time-value-input"
                                    value={formData.timeValue}
                                    onChange={(e) => updateField('timeValue', parseInt(e.target.value) || 1)}
                                    min={1}
                                    max={365}
                                />
                                <select
                                    className="time-unit-select"
                                    value={formData.timeUnit}
                                    onChange={(e) => updateField('timeUnit', e.target.value)}
                                >
                                    {TIME_UNITS.map(unit => (
                                        <option key={unit.value} value={unit.value}>
                                            {unit.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {/* Field name/value — shown for FieldChange trigger */}
                    {currentTrigger?.requiresField && (
                        <>
                            <div className="form-group">
                                <label htmlFor="fieldName">Which field?</label>
                                <select
                                    id="fieldName"
                                    value={formData.fieldName}
                                    onChange={(e) => updateField('fieldName', e.target.value)}
                                >
                                    {FIELD_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="fieldValue">Changes to what value?</label>
                                <input
                                    id="fieldValue"
                                    type="text"
                                    value={formData.fieldValue}
                                    onChange={(e) => updateField('fieldValue', e.target.value)}
                                    placeholder="e.g., Critical, Completed, etc."
                                />
                            </div>
                        </>
                    )}

                    {/* Priority value — for OnPriorityChange trigger */}
                    {currentTrigger?.requiresPriorityValue && (
                        <div className="form-group">
                            <label htmlFor="priorityValue">When priority changes to:</label>
                            <select
                                id="priorityValue"
                                value={formData.priorityValue}
                                onChange={(e) => updateField('priorityValue', e.target.value)}
                            >
                                <option value="">— Any priority (trigger on any change) —</option>
                                {PRIORITY_OPTIONS.map(p => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Recurrence config — for Recurring trigger */}
                    {currentTrigger?.requiresRecurrence && (
                        <div className="form-group">
                            <label>Run every:</label>
                            <div className="time-input-row">
                                <input
                                    type="number"
                                    className="time-value-input"
                                    value={formData.recurrenceInterval}
                                    onChange={(e) => updateField('recurrenceInterval', parseInt(e.target.value) || 1)}
                                    min={1}
                                    max={1440}
                                />
                                <select
                                    className="time-unit-select"
                                    value={formData.recurrenceUnit}
                                    onChange={(e) => updateField('recurrenceUnit', e.target.value)}
                                >
                                    <option value="minutes">Minutes</option>
                                    <option value="hours">Hours</option>
                                    <option value="days">Days</option>
                                </select>
                            </div>
                            <small className="form-hint">How often the rule should check and execute for matching items</small>
                        </div>
                    )}
                </div>

                {/* ── CONDITIONS (Optional filter) Section ── */}
                <div className="rule-builder-section conditions-section">
                    <div className="rule-builder-label">
                        <span className="rule-builder-step-number">3</span>
                        <span className="rule-builder-badge conditions-badge"><FaFilter size={10} /> IF</span>
                        <span>Additional conditions <small className="optional-tag">(optional)</small></span>
                    </div>
                    <p className="section-description">
                        Only execute this rule when the following conditions are also true.
                    </p>

                    {formData.conditions.length > 0 && (
                        <>
                            <div className="conditions-logic-toggle">
                                <button
                                    type="button"
                                    className={`logic-btn ${formData.conditionLogic === 'AND' ? 'active' : ''}`}
                                    onClick={() => updateField('conditionLogic', 'AND')}
                                >
                                    ALL conditions must match
                                </button>
                                <button
                                    type="button"
                                    className={`logic-btn ${formData.conditionLogic === 'OR' ? 'active' : ''}`}
                                    onClick={() => updateField('conditionLogic', 'OR')}
                                >
                                    ANY condition must match
                                </button>
                            </div>
                            <div className="conditions-list">
                                {formData.conditions.map((cond, idx) => (
                                    <div key={idx} className="condition-row">
                                        <select
                                            value={cond.field}
                                            onChange={(e) => {
                                                const updated = [...formData.conditions];
                                                updated[idx] = { ...updated[idx], field: e.target.value };
                                                updateField('conditions', updated);
                                            }}
                                            className="condition-field-select"
                                        >
                                            <option value="">— Field —</option>
                                            {FIELD_OPTIONS.map(f => (
                                                <option key={f.value} value={f.value}>{f.label}</option>
                                            ))}
                                        </select>
                                        <select
                                            value={cond.operator}
                                            onChange={(e) => {
                                                const updated = [...formData.conditions];
                                                updated[idx] = { ...updated[idx], operator: e.target.value };
                                                updateField('conditions', updated);
                                            }}
                                            className="condition-operator-select"
                                        >
                                            {CONDITION_OPERATORS.map(op => (
                                                <option key={op.value} value={op.value}>{op.label}</option>
                                            ))}
                                        </select>
                                        {!['isEmpty', 'isNotEmpty'].includes(cond.operator) && (
                                            <input
                                                type="text"
                                                value={cond.value || ''}
                                                onChange={(e) => {
                                                    const updated = [...formData.conditions];
                                                    updated[idx] = { ...updated[idx], value: e.target.value };
                                                    updateField('conditions', updated);
                                                }}
                                                className="condition-value-input"
                                                placeholder="Value..."
                                            />
                                        )}
                                        <button
                                            type="button"
                                            className="btn-icon-small btn-danger"
                                            onClick={() => {
                                                const updated = formData.conditions.filter((_, i) => i !== idx);
                                                updateField('conditions', updated);
                                            }}
                                            title="Remove condition"
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                    <button
                        type="button"
                        className="btn-add-condition"
                        onClick={() => {
                            updateField('conditions', [
                                ...formData.conditions,
                                { field: 'Priority', operator: 'equals', value: '' }
                            ]);
                        }}
                    >
                        <FaPlus /> Add Condition
                    </button>
                </div>

                {/* ── Arrow Connector ── */}
                <div className="rule-builder-arrow">
                    <FaArrowRight size={24} />
                </div>

                {/* ── THEN (Action) Section ── */}
                <div className="rule-builder-section action-section">
                    <div className="rule-builder-label">
                        <span className="rule-builder-step-number">4</span>
                        <span className="rule-builder-badge then-badge">THEN</span>
                        <span>What should happen?</span>
                    </div>

                    <div className="form-group">
                        <label htmlFor="actionType">Action</label>
                        <select
                            id="actionType"
                            value={formData.actionType}
                            onChange={(e) => updateField('actionType', e.target.value)}
                            className="action-type-select"
                        >
                            {Object.entries(
                                ACTION_TYPES.reduce((groups, action) => {
                                    const cat = action.category || 'Other';
                                    if (!groups[cat]) groups[cat] = [];
                                    groups[cat].push(action);
                                    return groups;
                                }, {})
                            ).map(([category, actions]) => (
                                <optgroup key={category} label={category}>
                                    {actions.map(action => (
                                        <option key={action.value} value={action.value}>
                                            {action.label}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                        <small className="form-hint">
                            <FaInfoCircle /> {currentAction?.description}
                        </small>
                    </div>

                    {/* Destination column — hidden for NotifyOnly since no move occurs */}
                    {!currentAction?.noMove && (
                        <div className="form-group">
                            <label htmlFor="destinationColumn">Move item to which column? *</label>
                            <select
                                id="destinationColumn"
                                value={formData.destinationColumnId}
                                onChange={(e) => updateField('destinationColumnId', e.target.value)}
                            >
                                <option value="">— Select a column —</option>
                                {columns.map(col => (
                                    <option key={col.columnId} value={col.columnId}>
                                        {col.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Notification emails — for MoveAndNotify */}
                    {currentAction?.requiresNotify && (
                        <div className="form-group">
                            <label htmlFor="notifyEmails">Who should be notified? (email addresses)</label>
                            <input
                                id="notifyEmails"
                                type="text"
                                value={formData.notifyEmails}
                                onChange={(e) => updateField('notifyEmails', e.target.value)}
                                placeholder="e.g., john@company.com, jane@company.com"
                            />
                            <small className="form-hint">
                                Separate multiple email addresses with commas
                            </small>
                        </div>
                    )}

                    {/* User assignment — for MoveAndAssign */}
                    {currentAction?.requiresAssign && (
                        <div className="form-group">
                            <label htmlFor="assignUser">Assign to which user?</label>
                            <select
                                id="assignUser"
                                value={formData.assignToUserId}
                                onChange={(e) => updateField('assignToUserId', e.target.value)}
                            >
                                <option value="">— Select a user —</option>
                                {allUsers.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.firstName && user.lastName
                                            ? `${user.firstName} ${user.lastName}`
                                            : user.name || user.userEmail || `User ${user.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Comment text — for MoveAndComment */}
                    {currentAction?.requiresComment && (
                        <div className="form-group">
                            <label htmlFor="commentText">What comment should be added?</label>
                            <textarea
                                id="commentText"
                                value={formData.commentText}
                                onChange={(e) => updateField('commentText', e.target.value)}
                                placeholder="e.g., This item was automatically moved due to inactivity."
                                rows={2}
                                maxLength={1000}
                            />
                        </div>
                    )}

                    {/* Recipients — for NotifyOnly */}
                    {currentAction?.requiresRecipients && (
                        <div className="notify-recipients-section">
                            <div className="form-group">
                                <label>Email Template (optional)</label>
                                <select
                                    value={formData.emailTemplateKey}
                                    onChange={(e) => updateField('emailTemplateKey', e.target.value)}
                                >
                                    <option value="">— Default notification format —</option>
                                    {emailTemplates.map(tmpl => (
                                        <option key={tmpl.templateKey || tmpl.emailTemplateId} value={tmpl.templateKey}>
                                            {tmpl.displayName || tmpl.templateKey}
                                        </option>
                                    ))}
                                </select>
                                <small className="form-hint">Select a branded email template for the notification</small>
                            </div>

                            {/* Notify Users — Searchable chip selector */}
                            <div className="form-group">
                                <label>Notify Users</label>
                                {formData.notifyUserIds.length > 0 && (
                                    <div className="chip-tags">
                                        {formData.notifyUserIds.map(id => (
                                            <span key={`u${id}`} className="chip-tag chip-tag--user">
                                                {getUserName(id)}
                                                <button type="button" className="chip-tag__remove" onClick={() => toggleChip('notifyUserIds', id)} title="Remove">
                                                    <IoClose size={12} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="chip-search-wrapper" ref={userSearchRef}>
                                    <IoSearch size={14} className="chip-search-icon" />
                                    <input
                                        className="chip-search-input"
                                        placeholder="Search users..."
                                        value={userSearch}
                                        onChange={e => { setUserSearch(e.target.value); setShowUserDropdown(true); }}
                                        onFocus={() => setShowUserDropdown(true)}
                                    />
                                    {showUserDropdown && (() => {
                                        const filtered = allUsers.filter(u =>
                                            !formData.notifyUserIds.includes(u.id) &&
                                            (!userSearch || (u.name || u.email || '').toLowerCase().includes(userSearch.toLowerCase()))
                                        );
                                        return (
                                            <div className="chip-dropdown">
                                                {filtered.length === 0 ? (
                                                    <div className="chip-dropdown__empty">{userSearch ? 'No matching users' : 'All users selected'}</div>
                                                ) : filtered.map(u => (
                                                    <div
                                                        key={u.id}
                                                        className="chip-dropdown__item"
                                                        onClick={() => { toggleChip('notifyUserIds', u.id); setUserSearch(''); setShowUserDropdown(false); }}
                                                    >
                                                        {u.name || u.email}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>
                                <small className="form-hint">Search and select users to notify</small>
                            </div>

                            {/* Notify Departments — Searchable chip selector */}
                            <div className="form-group">
                                <label>Notify Departments</label>
                                {formData.notifyDepartmentIds.length > 0 && (
                                    <div className="chip-tags">
                                        {formData.notifyDepartmentIds.map(id => (
                                            <span key={`d${id}`} className="chip-tag chip-tag--dept">
                                                {getDeptName(id)}
                                                <button type="button" className="chip-tag__remove" onClick={() => toggleChip('notifyDepartmentIds', id)} title="Remove">
                                                    <IoClose size={12} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="chip-search-wrapper" ref={deptSearchRef}>
                                    <IoSearch size={14} className="chip-search-icon" />
                                    <input
                                        className="chip-search-input"
                                        placeholder="Search departments..."
                                        value={deptSearch}
                                        onChange={e => { setDeptSearch(e.target.value); setShowDeptDropdown(true); }}
                                        onFocus={() => setShowDeptDropdown(true)}
                                    />
                                    {showDeptDropdown && (() => {
                                        const filtered = allDepartments.filter(d =>
                                            !formData.notifyDepartmentIds.includes(d.departmentID || d.id) &&
                                            (!deptSearch || (d.departmentName || d.name || '').toLowerCase().includes(deptSearch.toLowerCase()))
                                        );
                                        return (
                                            <div className="chip-dropdown">
                                                {filtered.length === 0 ? (
                                                    <div className="chip-dropdown__empty">{deptSearch ? 'No matching departments' : 'All departments selected'}</div>
                                                ) : filtered.map(d => (
                                                    <div
                                                        key={d.departmentID || d.id}
                                                        className="chip-dropdown__item"
                                                        onClick={() => { toggleChip('notifyDepartmentIds', d.departmentID || d.id); setDeptSearch(''); setShowDeptDropdown(false); }}
                                                    >
                                                        {d.departmentName || d.name}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>
                                <small className="form-hint">All users in selected departments will be notified</small>
                            </div>

                            {/* Notify Roles — Searchable chip selector */}
                            <div className="form-group">
                                <label>Notify Roles</label>
                                {formData.notifyRoleIds.length > 0 && (
                                    <div className="chip-tags">
                                        {formData.notifyRoleIds.map(id => (
                                            <span key={`r${id}`} className="chip-tag chip-tag--role">
                                                {getRoleName(id)}
                                                <button type="button" className="chip-tag__remove" onClick={() => toggleChip('notifyRoleIds', id)} title="Remove">
                                                    <IoClose size={12} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="chip-search-wrapper" ref={roleSearchRef}>
                                    <IoSearch size={14} className="chip-search-icon" />
                                    <input
                                        className="chip-search-input"
                                        placeholder="Search roles..."
                                        value={roleSearch}
                                        onChange={e => { setRoleSearch(e.target.value); setShowRoleDropdown(true); }}
                                        onFocus={() => setShowRoleDropdown(true)}
                                    />
                                    {showRoleDropdown && (() => {
                                        const filtered = allRoles.filter(r =>
                                            !formData.notifyRoleIds.includes(r.roleID || r.id) &&
                                            (!roleSearch || (r.roleName || r.name || '').toLowerCase().includes(roleSearch.toLowerCase()))
                                        );
                                        return (
                                            <div className="chip-dropdown">
                                                {filtered.length === 0 ? (
                                                    <div className="chip-dropdown__empty">{roleSearch ? 'No matching roles' : 'All roles selected'}</div>
                                                ) : filtered.map(r => (
                                                    <div
                                                        key={r.roleID || r.id}
                                                        className="chip-dropdown__item"
                                                        onClick={() => { toggleChip('notifyRoleIds', r.roleID || r.id); setRoleSearch(''); setShowRoleDropdown(false); }}
                                                    >
                                                        {r.roleName || r.name}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>
                                <small className="form-hint">All users with selected roles will be notified</small>
                            </div>
                        </div>
                    )}

                    {/* SetField action config */}
                    {currentAction?.requiresSetField && (
                        <>
                            <div className="form-group">
                                <label>Which field to set?</label>
                                <select
                                    value={formData.setFieldName}
                                    onChange={(e) => updateField('setFieldName', e.target.value)}
                                >
                                    <option value="">— Select a field —</option>
                                    {FIELD_OPTIONS.map(f => (
                                        <option key={f.value} value={f.value}>{f.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Set to what value?</label>
                                {formData.setFieldName === 'Priority' ? (
                                    <select
                                        value={formData.setFieldValue}
                                        onChange={(e) => updateField('setFieldValue', e.target.value)}
                                    >
                                        <option value="">— Select priority —</option>
                                        {PRIORITY_OPTIONS.map(p => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </select>
                                ) : formData.setFieldName === 'Status' ? (
                                    <select
                                        value={formData.setFieldValue}
                                        onChange={(e) => updateField('setFieldValue', e.target.value)}
                                    >
                                        <option value="">— Select status —</option>
                                        {columns.map(col => (
                                            <option key={col.columnId} value={col.statusValue || col.name}>
                                                {col.name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        value={formData.setFieldValue}
                                        onChange={(e) => updateField('setFieldValue', e.target.value)}
                                        placeholder="Enter value..."
                                    />
                                )}
                            </div>
                        </>
                    )}

                    {/* ChangePriority action config */}
                    {currentAction?.requiresPriority && (
                        <div className="form-group">
                            <label>Change priority to:</label>
                            <select
                                value={formData.newPriority}
                                onChange={(e) => updateField('newPriority', e.target.value)}
                            >
                                <option value="">— Select priority —</option>
                                {PRIORITY_OPTIONS.map(p => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* CreateChildItem action config */}
                    {currentAction?.requiresChildConfig && (
                        <>
                            <div className="form-group">
                                <label>Child item title:</label>
                                <input
                                    type="text"
                                    value={formData.childTitle}
                                    onChange={(e) => updateField('childTitle', e.target.value)}
                                    placeholder="e.g., Follow-up: {{Title}}"
                                />
                                <small className="form-hint">Use {'{{Title}}'} to reference the parent item&apos;s title</small>
                            </div>
                            <div className="form-group">
                                <label>Assign to department (optional):</label>
                                <select
                                    value={formData.childAssignToDepartment}
                                    onChange={(e) => updateField('childAssignToDepartment', e.target.value)}
                                >
                                    <option value="">— No department —</option>
                                    {allDepartments.map(d => (
                                        <option key={d.departmentID || d.id} value={d.departmentName || d.name}>
                                            {d.departmentName || d.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}

                    {/* DuplicateItem action config */}
                    {currentAction?.requiresDuplicateConfig && (
                        <>
                            <div className="form-group">
                                <label>Title prefix:</label>
                                <input
                                    type="text"
                                    value={formData.duplicateTitlePrefix}
                                    onChange={(e) => updateField('duplicateTitlePrefix', e.target.value)}
                                    placeholder="[COPY] "
                                />
                                <small className="form-hint">Text to prepend to the duplicate item&apos;s title. Defaults to &quot;[COPY] &quot;.</small>
                            </div>
                            <div className="form-group form-group--checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.duplicateCopyComments === true}
                                        onChange={(e) => updateField('duplicateCopyComments', e.target.checked)}
                                    />
                                    &nbsp;Copy comments to duplicate
                                </label>
                            </div>
                        </>
                    )}

                    {/* CallWebhook action config */}
                    {currentAction?.requiresWebhook && (
                        <>
                            <div className="form-group">
                                <label>Webhook URL *</label>
                                <input
                                    type="url"
                                    value={formData.webhookUrl}
                                    onChange={(e) => updateField('webhookUrl', e.target.value)}
                                    placeholder="https://hooks.example.com/automation"
                                />
                            </div>
                            <div className="form-group">
                                <label>HTTP Method</label>
                                <select
                                    value={formData.webhookMethod}
                                    onChange={(e) => updateField('webhookMethod', e.target.value)}
                                >
                                    <option value="POST">POST</option>
                                    <option value="PUT">PUT</option>
                                    <option value="PATCH">PATCH</option>
                                </select>
                            </div>
                        </>
                    )}
                </div>

                {/* ── Options ── */}
                <div className="rule-options-row">
                    <div className="form-group priority-group">
                        <label htmlFor="priority">Execution Order</label>
                        <input
                            id="priority"
                            type="number"
                            value={formData.priority}
                            onChange={(e) => updateField('priority', parseInt(e.target.value) || 0)}
                            min={0}
                        />
                        <small className="form-hint">Lower numbers run first</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="maxExec">Max executions per item</label>
                        <input
                            id="maxExec"
                            type="number"
                            value={formData.maxExecutionsPerItem}
                            onChange={(e) => updateField('maxExecutionsPerItem', parseInt(e.target.value) || 0)}
                            min={0}
                        />
                        <small className="form-hint">0 = unlimited. Prevents infinite loops for recurring rules.</small>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-label toggle-label">
                            <input
                                type="checkbox"
                                checked={formData.isActive}
                                onChange={(e) => updateField('isActive', e.target.checked)}
                            />
                            <span>Enable this rule immediately</span>
                        </label>
                    </div>
                </div>
            </div>

            <div className="form-footer">
                <button className="btn-secondary" onClick={() => setShowRuleForm(false)}>
                    Cancel
                </button>
                <button className="btn-primary" onClick={handleSaveRule}>
                    <FaSave /> {editingRule ? 'Update Rule' : 'Create Rule'}
                </button>
            </div>
        </div>
    );

    // ──────────────────────────────────────────────────────────────────────
    // Render: Rules List (card-based with natural language summaries)
    // ──────────────────────────────────────────────────────────────────────
    const renderRulesList = () => (
        <div className="automation-rules-list">
            <div className="rules-list-header">
                <h3>Automation Rules ({rules.length})</h3>
                <button className="btn-primary-small" onClick={handleCreateRule}>
                    <FaPlus /> Add Rule
                </button>
            </div>

            {loading ? (
                <div className="loading-state">Loading automation rules...</div>
            ) : rules.length === 0 ? (
                <div className="empty-state">
                    <FaBolt size={48} color="#cbd5e0" />
                    <p className="empty-state-title">No automation rules yet</p>
                    <p>
                        Automate your workflow by creating rules that move action items
                        between columns based on triggers like time, status changes, or field values.
                    </p>
                    <button className="btn-primary" onClick={handleCreateRule}>
                        <FaPlus /> Create Your First Rule
                    </button>
                </div>
            ) : (
                <div className="rules-list">
                    {rules.map((rule) => {
                        const summary = buildRuleSummary(rule);
                        return (
                            <div key={rule.ruleId} className={`rule-card ${!rule.isActive ? 'inactive' : ''}`}>
                                <div className="rule-card-header">
                                    <div className="rule-card-title">
                                        <h4>{rule.name}</h4>
                                        {rule.description && (
                                            <p className="rule-card-desc">{rule.description}</p>
                                        )}
                                    </div>
                                    <div className="rule-actions">
                                        <button
                                            className={`btn-icon-small ${rule.isActive ? 'active' : 'inactive'}`}
                                            onClick={() => handleToggleRule(rule.ruleId)}
                                            title={rule.isActive ? 'Disable rule' : 'Enable rule'}
                                        >
                                            {rule.isActive ? <FaToggleOn size={22} /> : <FaToggleOff size={22} />}
                                        </button>
                                        <button
                                            className="btn-icon-small"
                                            onClick={() => handleEditRule(rule)}
                                            title="Edit rule"
                                        >
                                            <FaEdit />
                                        </button>
                                        <button
                                            className="btn-icon-small btn-danger"
                                            onClick={() => handleDeleteRule(rule.ruleId)}
                                            title="Delete rule"
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                </div>

                                {/* Visual "When → Then" summary */}
                                <div className="rule-flow-summary">
                                    <div className="rule-flow-when">
                                        <span className="rule-flow-badge when-badge">WHEN</span>
                                        <span className="rule-flow-icon">{summary.triggerIcon}</span>
                                        <span className="rule-flow-text">Item {summary.triggerText}</span>
                                    </div>
                                    <div className="rule-flow-arrow">
                                        <FaArrowRight />
                                    </div>
                                    <div className="rule-flow-then">
                                        <span className="rule-flow-badge then-badge">THEN</span>
                                        <span className="rule-flow-text">{summary.actionText}</span>
                                    </div>
                                </div>

                                {/* Metadata footer */}
                                <div className="rule-card-footer">
                                    <span className={`rule-status-badge ${rule.isActive ? 'active' : 'inactive'}`}>
                                        {rule.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                    {rule.executionCount > 0 && (
                                        <span className="rule-exec-count">
                                            Run {rule.executionCount} time{rule.executionCount !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {rule.lastExecutedDate && (
                                        <span className="rule-last-run">
                                            Last run: {new Date(rule.lastExecutedDate).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    // ──────────────────────────────────────────────────────────────────────
    // Main Render
    // ──────────────────────────────────────────────────────────────────────

    // When rendered inline (inside BoardSettingsModal tab), skip the modal overlay
    if (inline) {
        return (
            <div className="automation-rules-inline">
                {showRuleForm ? renderRuleForm() : renderRulesList()}
            </div>
        );
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-large automation-rules-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        <FaBolt /> Automations — {board.name}
                    </h2>
                    <button className="close-button" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="modal-body">
                    {showRuleForm ? renderRuleForm() : renderRulesList()}
                </div>
            </div>
        </div>
    );
};

export default AutomationRulesModal;
